import React from "react";
import i18next from "src/i18n/config";
import { useDispatch } from "src/store";
import { downloadFromBlob } from "src/ui/utils/download";

import productsApi from "@dashboard/products/api/apiClient";
import { Build } from "@dashboard/products/types";
import { showErrorToast, showWarningToast } from "@shared/toasts/Toasts";

import { calculateDownloadProgress, calculateDownloadSpeed } from "../../shared";
import { countDownloads } from "../countDownloads";
import { getFirmwareArtifacts } from "../firmwareDownloads";

const handleDownloadError = (error: unknown) => {
    const err = error as Error;
    if (err.name === "AbortError" || err.name === "CanceledError" || err.message === "Failed to fetch") {
        showWarningToast(i18next.t("pages:productPage.buildsTable.toast.downloadCancelled"));
    } else {
        const message = err.message || i18next.t("pages:productPage.buildsTable.toast.exportFailed");
        showErrorToast(message);
    }
};

export const useExportFile = (build: Build) => {
    const [name, setName] = React.useState<string | null>(null);
    const [speed, setSpeed] = React.useState("0 MB/s");
    const [progress, setProgress] = React.useState(0);
    const dispatch = useDispatch();

    const abortController = React.useRef(new AbortController());

    const onClear = () => {
        setProgress(0);
        setName(null);
        setSpeed("0 MB/s");
    };
    const onAbort = () => {
        onClear();
        abortController.current.abort();
    };
    const onExport = () => {
        abortController.current = new AbortController();

        let fileName = "export.zip";
        const downloadStartTime = Date.now();

        productsApi
            .downloadBuild(build.productId, build.buildId, abortController.current.signal)
            .then((response) => {
                if (!response.ok) {
                    throw new Error("Network response was not ok");
                }

                const contentDisposition = response.headers.get("content-disposition");
                const tmpName = contentDisposition?.split("filename=")[1]?.replace(/^["']|["']$/g, "");
                if (tmpName) {
                    fileName = tmpName;
                    setName(tmpName);
                }

                const contentLength = response.headers.get("x-estimated-length");
                if (!contentLength) {
                    throw new Error("x-estimated-length header is missing");
                }

                let loaded = 0;
                const total = parseInt(contentLength, 10);
                const reader = response.body?.getReader();

                return new ReadableStream({
                    start(controller) {
                        function push() {
                            void reader
                                ?.read()
                                .then(({ done, value }) => {
                                    if (done) {
                                        controller.close();
                                        return;
                                    }

                                    loaded += value.length;
                                    const tmpProgress = calculateDownloadProgress(loaded, total);
                                    setProgress(tmpProgress);

                                    const tmpSpeed = calculateDownloadSpeed(loaded, downloadStartTime);
                                    setSpeed(tmpSpeed);

                                    controller.enqueue(value);
                                    push();
                                })
                                .catch((error: unknown) => {
                                    controller.error(error);
                                });
                        }

                        push();
                    },
                });
            })
            .then((stream) => new Response(stream))
            .then((response) => response.blob())
            .then((blob) => {
                downloadFromBlob(blob, fileName);
                dispatch(
                    countDownloads(
                        build,
                        getFirmwareArtifacts(build).map((artifact) => artifact.id),
                    ),
                );
            })
            .catch(handleDownloadError)
            .finally(onClear);
    };

    return { name, speed, progress, onExport, onAbort };
};
